import { I18n } from "@assets/strings";
import { Map, Set } from "immutable";
import React, { useState } from "react";
import { Link } from "react-router";
import styled from "styled-components";
import { Path } from "@core/routing/path";
import { useServices } from "@core/servicesContext";
import { Quiz } from "@domain/lms/quiz/quiz";
import { useAsync } from "@utils/hooks";
import { theme } from "@ui/styles/theme";
import { mobile } from "@ui/styles/utils";
import { LoadingButton } from "@ui/components/buttons/loadingButton";
import { TertiaryButton } from "@ui/components/buttons/tertiaryButton";
import { DraftRenderer } from "@ui/components/containers/draftRenderer";
import { Title } from "@ui/components/containers/title";
import { ExerciseView } from "@ui/components/course/exercise/exerciseView";
import { Row, stack } from "@ui/components/layout";
import { QuizEnding } from "./quizEnding";
interface QuizViewProps extends React.HTMLAttributes<HTMLDivElement> {
	quiz: Quiz;
	isAuthor?: boolean;
	onFinish?: () => void;
}
export const QuizView: React.FC<QuizViewProps> = ({ quiz, onFinish, isAuthor, ...htmlProps }) => {
	const { quizService } = useServices();

	const initialAnswers = quiz.exercises.map((ex): [string, Set<number>] => [ex.id, Set()]).toArray();
	const [answers, setAnswers] = useState<Map<string, Set<number>>>(Map(initialAnswers));

	const participantAnswers = isAuthor ? quiz.participantDetails.answers : quiz?.participants.answers;

	const valid = answers.every((a) => a?.size > 0);
	const usedExercises = quiz.exercises.toArray();
	const isFinished = quiz.participants.byMe;

	const { run, loading } = useAsync(async () => {
		await quizService.submit(quiz.id, answers as Map<string, Set<number>>);
		onFinish?.();
	}, [quiz.id, answers]);

	return (
		<Container {...htmlProps}>
			{isFinished || isAuthor ? (
				<>
					<QuizEnding quiz={quiz} isAuthor={isAuthor} />
					<Separator />
					<Title>{I18n.t("quiz.details.title")}</Title>
				</>
			) : (
				<>
					<Title>{quiz.title}</Title>
					{quiz.description.hasText() && (
						<DraftRenderer contractibleFold fold foldColor={theme.colors.darkgray} content={quiz.description} />
					)}
					{/* <Description>{quiz.description.getPlainText()}</Description> */}
					<CustomSeparator />
				</>
			)}
			{usedExercises.map((ex, i) => (
				<ExerciseView
					key={ex.id}
					exercise={ex}
					index={i}
					currentAnswer={
						participantAnswers && participantAnswers.length > 0
							? Set(participantAnswers[0].answerIndices)
							: answers.get(ex.id, Set())
					}
					onAnswerChosen={(answer) => setAnswers((prevAnswers) => prevAnswers.set(ex.id, answer))}
				/>
			))}
			<Row gap={10} justify="space-between" align="center">
				<Link to={Path.Root}>
					<QuitButton>{I18n.t("quit")}</QuitButton>
				</Link>
				{!isFinished && (
					<LoadingButton onClick={run} loading={loading} disabled={!valid}>
						{I18n.t("validate")}
					</LoadingButton>
				)}
			</Row>
		</Container>
	);
};

const Container = styled.div`
	${stack(20, "flex-start", "stretch")};
`;

const QuitButton = styled(TertiaryButton)`
	text-decoration: none;
	color: ${({ theme }) => theme.colors.primary};
	&:hover {
		color: ${({ theme }) => theme.colors.primaryLight};
	}
`;

const Separator = styled.div`
	margin: 40px -40px;
	@media ${mobile} {
		margin: 30px -15px;
	}
	background-color: ${({ theme }) => theme.colors.lightergray};
	height: 1px;
`;

const CustomSeparator = styled.div`
	margin: 0px -40px;
	@media ${mobile} {
		margin: 0px -15px;
	}
	background-color: ${({ theme }) => theme.colors.lightergray};
	height: 1px;
`;
